import React from 'react';
import T from 'prop-types';
import {connect} from 'react-redux';
import cn from 'classnames';
import Modal from 'components/Modals/Modal';
import AnimateLocationDelivery from 'assets/animate-delivery-red.gif';
import AnimateLocationPickup from 'assets/animate-pizza-red.gif';
import {ButtonOutline, ButtonPrimary} from 'components/buttons';
import {
  OPEN_CONFIRMATION_MODAL,
  PUBLISH_LOCATION,
  SET_STATUS_BUTTON,
  UNPAUSE_LOCATION
} from 'store/locationSettings/action';
import {UberEatsImage} from 'components/UberEatsImage/styles';
import DialogTitle from 'components/DialogTitle/styles';
import DialogAction from 'components/DialogAction/styles';
import DialogActions from 'components/DialogActions/styles';
import {
  SHOW_PUBLISH_PROGRESSBAR,
  SHOW_UNPUBLISH_BANNER,
  STOP_GET_PUBLISH_STATUS_JOB
} from 'store/global/globalNotifications/action';
import DISABLE_ENTITY_NAMES, {
  getProviderDisplayName
} from 'helpers/constants/storeSettings';
import {
  buildExternalOrderProvidersPayload,
  buildUnpauseLocationBody,
  getPauseSettings,
  removePauseSetting
} from 'helpers/functionUtils/storeSettings';

class LocationConfirmationModal extends React.Component {
  onClose = () => {
    this.props.OPEN_CONFIRMATION_MODAL({
      open: false,
      entityName: '',
      providerName: null,
      pauseAllChannels: false
    });
  };

  publishLocation = () => {
    this.props.dispatch(
      PUBLISH_LOCATION({
        preCallback: () => {
          this.props.dispatch(
            SHOW_UNPUBLISH_BANNER({showBanner: false, types: []})
          );
          this.props.dispatch(SHOW_PUBLISH_PROGRESSBAR(true));
          this.onClose();
        },
        postCallBack: () => {},
        errorCallback: () => {
          this.props.dispatch(STOP_GET_PUBLISH_STATUS_JOB());
          this.props.dispatch(SHOW_PUBLISH_PROGRESSBAR(false));
          this.onClose();
        }
      })
    );
  };

  onEnable = () => {
    const {
      entityName,
      providerName,
      pauseAllChannels,
      setFieldValue,
      values,
      closedManually,
      dispatch,
      resetForm,
      setFormSubmitting,
      externalOrderProviders
    } = this.props;

    if (entityName === 'Store' && providerName) {
      dispatch(SET_STATUS_BUTTON(true));

      const pauseSettings = removePauseSetting(
        getPauseSettings(
          values?.ExternalOrderProviders || externalOrderProviders
        ),
        providerName
      );
      const providersPayload = buildExternalOrderProvidersPayload(
        values?.ExternalOrderProviders || externalOrderProviders,
        pauseSettings
      );
      const channelObj = {
        ExternalOrderProviders: providersPayload
      };

      setFieldValue('ExternalOrderProviders', providersPayload);
      setFormSubmitting(false);

      dispatch(
        UNPAUSE_LOCATION({
          payload: buildUnpauseLocationBody({channelNames: [providerName]}),
          optimisticPayload: channelObj,
          successCallback: () => {
            this.publishLocation();
          },
          errorCallback: () => {
            dispatch(STOP_GET_PUBLISH_STATUS_JOB());
            dispatch(SHOW_PUBLISH_PROGRESSBAR(false));
          }
        })
      );
      this.onClose();
      return;
    }

    if (entityName === 'Store') {
      dispatch(SET_STATUS_BUTTON(true));

      const storeObj = {
        ClosedManually: false,
        ClosedManuallyTime: null,
        'notSyncedData.ClosedManually': false,
        ClosedManuallyReason: null,
        StoreDisableOption: null,
        ClosedManuallyUntilTime: null
      };

      const pausedChannelNames = pauseAllChannels
        ? getPauseSettings(
            values?.ExternalOrderProviders || externalOrderProviders
          )
            .map((setting) => setting?.Name)
            .filter(Boolean)
        : [];

      if (pauseAllChannels) {
        const providersPayload = buildExternalOrderProvidersPayload(
          values?.ExternalOrderProviders || externalOrderProviders,
          []
        );
        storeObj.ExternalOrderProviders = providersPayload;
        setFieldValue('ExternalOrderProviders', providersPayload);
      }

      const unpauseStore = () => {
        setFormSubmitting(false);
        dispatch(
          UNPAUSE_LOCATION({
            payload: buildUnpauseLocationBody({
              openRestaurant: true,
              channelNames: pausedChannelNames
            }),
            optimisticPayload: storeObj,
            successCallback: () => {
              this.publishLocation();
            },
            errorCallback: () => {
              dispatch(STOP_GET_PUBLISH_STATUS_JOB());
              dispatch(SHOW_PUBLISH_PROGRESSBAR(false));
            }
          })
        );
      };

      if (values.ClosedManually && closedManually) {
        resetForm({...values, storeObj});
        Object.entries(storeObj).forEach(([key, value]) => {
          if (key !== 'ExternalOrderProviders') {
            setFieldValue(key, value);
          }
        });
        unpauseStore();
      } else if (pauseAllChannels) {
        unpauseStore();
      } else {
        Object.entries(storeObj).forEach(([key, value]) => {
          setFieldValue(key, value);
        });
        setFormSubmitting(false);
        this.onClose();
      }
    } else {
      const entityObj = {
        [`DeliveryOptions.${entityName}`]: true,
        [`notSyncedData.DeliveryOptions.${entityName}`]: true,
        [`${entityName}EnableIn`]: null,
        [`${entityName}DisableReason`]: null,
        [`${entityName}DisableOption`]: null
      };
      Object.entries(entityObj).forEach(([key, value]) => {
        setFieldValue(key, value);
      });
      this.onClose();
    }
  };

  render() {
    const {
      isOpen,
      entityName,
      providerName,
      pauseAllChannels,
      disableButton
    } = this.props;
    const channelLabel = providerName
      ? getProviderDisplayName(providerName)
      : null;

    const getTitle = () => {
      if (entityName === DISABLE_ENTITY_NAMES.STORE && channelLabel) {
        return (
          <>
            Unpause <span>{channelLabel}?</span>
          </>
        );
      }
      if (entityName === DISABLE_ENTITY_NAMES.STORE && pauseAllChannels) {
        return (
          <>
            Unpause <span>All Channels?</span>
          </>
        );
      }
      if (entityName === DISABLE_ENTITY_NAMES.STORE) {
        return (
          <>
            Unpause <span>Orders?</span>
          </>
        );
      }
      return (
        <>
          Enable <span>{entityName}?</span>
        </>
      );
    };

    const getConfirmLabel = () => {
      if (
        entityName === DISABLE_ENTITY_NAMES.STORE &&
        (channelLabel || pauseAllChannels)
      ) {
        return 'Yes, Unpause';
      }
      if (entityName === DISABLE_ENTITY_NAMES.STORE) {
        return 'Yes, Unpause Orders';
      }
      return `Yes, Enable ${entityName}`;
    };

    return (
      <>
        {isOpen && (
          <Modal
            onCloseAction={() => this.onClose()}
            title={
              <DialogTitle className='marginBottom2l'>{getTitle()}</DialogTitle>
            }
            width='715px'
            body={
              <>
                <UberEatsImage
                  src={
                    entityName === 'Delivery'
                      ? AnimateLocationDelivery
                      : AnimateLocationPickup
                  }
                />
                <DialogActions>
                  <DialogAction className='w50'>
                    <ButtonOutline onClick={() => this.onClose()}>
                      {entityName === DISABLE_ENTITY_NAMES.STORE ? (
                        <span>Keep Paused</span>
                      ) : (
                        <span>Keep it Disabled</span>
                      )}
                    </ButtonOutline>
                  </DialogAction>
                  <DialogAction className='w50'>
                    <ButtonPrimary
                      className={cn('', {
                        disabled: disableButton
                      })}
                      onClick={() => {
                        this.onEnable();
                      }}
                    >
                      <span>{getConfirmLabel()}</span>
                    </ButtonPrimary>
                  </DialogAction>
                </DialogActions>
              </>
            }
          />
        )}
      </>
    );
  }
}

LocationConfirmationModal.propTypes = {
  isOpen: T.bool.isRequired,
  entityName: T.string,
  providerName: T.string,
  pauseAllChannels: T.bool,
  setFieldValue: T.func.isRequired,
  OPEN_CONFIRMATION_MODAL: T.func.isRequired,
  values: T.object.isRequired,
  closedManually: T.bool.isRequired,
  dispatch: T.func.isRequired,
  resetForm: T.func.isRequired,
  setFormSubmitting: T.func.isRequired,
  disableButton: T.bool.isRequired,
  externalOrderProviders: T.object
};

LocationConfirmationModal.defaultProps = {
  entityName: '',
  providerName: null,
  pauseAllChannels: false,
  externalOrderProviders: null
};

export default connect(
  (store) => ({
    isOpen: store.locationSettingsReducer.isConfirmationModalOpen,
    entityName: store.locationSettingsReducer.confirmationEntityName,
    providerName: store.locationSettingsReducer.confirmationProviderName,
    pauseAllChannels: store.locationSettingsReducer.pauseAllChannels
  }),
  {
    OPEN_CONFIRMATION_MODAL
  }
)(LocationConfirmationModal);
